import { useEffect, useState } from "react";
import { Eye, Users, Link2, Target, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

export default function AnalyticsPanel() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/admin/analytics")
      .then((r) => setData(r.data))
      .catch(() => setError("Could not load analytics"));
  }, []);

  if (error) return <div data-testid="analytics-error" className="mt-6 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3">{error}</div>;
  if (!data) return <div className="mt-6 flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-sky-600" /></div>;

  const cards = [
    { icon: Eye, label: "Views today", value: data.views_today, tid: "analytics-views-today" },
    { icon: Eye, label: "Views (7 days)", value: data.views_7d, tid: "analytics-views-7d" },
    { icon: Eye, label: "Views (30 days)", value: data.views_30d, tid: "analytics-views-30d" },
    { icon: Users, label: "Bookings (30 days)", value: data.bookings_30d, tid: "analytics-bookings-30d" },
  ];

  return (
    <div className="mt-6 space-y-6" data-testid="analytics-panel">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.label} data-testid={c.tid} className="rounded-xl bg-white border border-slate-200 p-5">
            <c.icon className="w-5 h-5 text-sky-600" />
            <div className="font-display text-3xl font-bold text-[#0A192F] mt-2">{c.value}</div>
            <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold mt-1">{c.label}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="rounded-xl bg-white border border-slate-200 p-6">
          <h3 className="font-semibold text-[#0A192F] flex items-center gap-2"><Eye className="w-4 h-4 text-sky-600" /> Top pages</h3>
          <ul className="mt-4 space-y-2 text-sm" data-testid="analytics-top-pages">
            {data.top_pages.length === 0 && <li className="text-slate-400">No data yet.</li>}
            {data.top_pages.map((p) => (
              <li key={p.page} className="flex justify-between gap-3">
                <span className="text-slate-600 truncate">{p.page}</span>
                <span className="font-semibold text-[#0A192F] shrink-0">{p.views}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-xl bg-white border border-slate-200 p-6">
          <h3 className="font-semibold text-[#0A192F] flex items-center gap-2"><Link2 className="w-4 h-4 text-sky-600" /> Where visitors come from</h3>
          <ul className="mt-4 space-y-2 text-sm" data-testid="analytics-referrers">
            {data.top_referrers.length === 0 && <li className="text-slate-400">No outside links yet — direct visits only.</li>}
            {data.top_referrers.map((r) => (
              <li key={r.site} className="flex justify-between gap-3">
                <span className="text-slate-600 truncate">{r.site}</span>
                <span className="font-semibold text-[#0A192F] shrink-0">{r.views}</span>
              </li>
            ))}
          </ul>
          <p className="text-xs text-slate-400 mt-4 leading-relaxed">Shows which sites (e.g. facebook.com, google.com) send you visitors.</p>
        </div>

        <div className="rounded-xl bg-white border border-slate-200 p-6">
          <h3 className="font-semibold text-[#0A192F] flex items-center gap-2"><Target className="w-4 h-4 text-sky-600" /> Campaign tracking</h3>
          <ul className="mt-4 space-y-2 text-sm" data-testid="analytics-utm">
            {data.utm_sources.length === 0 && <li className="text-slate-400">No campaign links used yet.</li>}
            {data.utm_sources.map((u, i) => (
              <li key={i} className="flex justify-between gap-3">
                <span className="text-slate-600 truncate">{u.source}{u.campaign ? ` / ${u.campaign}` : ""}</span>
                <span className="font-semibold text-[#0A192F] shrink-0">{u.views}</span>
              </li>
            ))}
          </ul>
          <p className="text-xs text-slate-400 mt-4 leading-relaxed">
            Tip: when Tony posts a link, add <span className="font-mono">?utm_source=facebook</span> or <span className="font-mono">?utm_source=linkedin</span> to the end and you'll see exactly which posts bring visitors.
          </p>
        </div>
      </div>

      <p className="text-xs text-slate-400">Privacy-friendly: no names, emails, or personal data are collected — only page views, where visitors came from, and campaign tags.</p>
    </div>
  );
}
